import { TableColumn } from '../../../../table/interfaces/table';
import { Job, JobPipeline, JobType } from '../../../interfaces/job';
import { JobNodeRelation } from '../../JobNodeRelation/JobNodeRelation';
import { JobModeIdBadge } from '../../JobModeIdBadge/JobModeIdBadge';
import { Calendar } from '../../../../shared/components/Calendar/Calendar';
import Duration from '../../../../shared/components/Duration/Duration';
import { RenderEventCell } from '../../EventCell';
import { GenericStatusLabel } from '../../../../shared/components/GenericStatusLabel/GenericStatusLabel';
import { LatencyWrapper } from '../../LatencyWrapper/LatencyWrapper';
import { Tooltip } from '../../../../tooltip/Tooltip';
import { getDurationString } from '../../../../shared/utils/date';

export const jobTableColumns = (withPipelineDetails: boolean): TableColumn<Job>[] => {
  return [
    ...(withPipelineDetails
      ? ([
          {
            id: 'pipeline',
            field: 'pipeline',
            headerName: 'Pipeline',
            minWidth: 206,
            width: 206,
            isFlexible: true,
            renderCell: params => (
              <JobNodeRelation
                mode={params.row.data.mode}
                pipeline={params.row.data.pipeline as JobPipeline}
                badgeText={params.row.data.isDeletedIntegration ? 'DELETED' : undefined}
              />
            )
          }
        ] as TableColumn<Job>[])
      : ([
          {
            id: 'mode',
            field: 'mode',
            headerName: 'Job ID',
            width: 90,
            renderCell: params => (
              <JobModeIdBadge mode={params.row.data.mode} jobId={params.row.data.jobId} collapsed />
            )
          }
        ] as TableColumn<Job>[])),
    {
      id: 'started_at',
      field: 'startedAt',
      headerName: 'Started At',
      width: 150,
      renderCell: params => (
        <Calendar value={params.row.data.startedAt} options={{ hideZeroTimestamp: true }} />
      ),
      canSort: true,
      sortKey: 'created_at'
    },
    {
      id: 'duration',
      field: 'duration',
      headerName: 'Duration',
      width: 108,
      renderCell: params => (
        <Tooltip
          enableOnTextOverflow
          dataId=''
          title={getDurationString(params.row.data.duration, true, true)}
        >
          <span className='text-ellipsis'>
            <Duration
              duration={params.row.data.duration}
              options={{ useAbbr: true, separateUnits: true }}
            />
          </span>
        </Tooltip>
      )
    },
    {
      id: 'latency',
      field: 'latency',
      headerName: 'Max Latency',
      width: 98,
      renderCell: params => {
        const latencyNotApplicable =
          params.row.data.isSkipped ||
          [JobType.HISTORICAL, JobType.TRUNCATE_AND_LOAD].includes(params.row.data.mode);
        return (
          <LatencyWrapper
            latency={params.row.data.latency}
            latencyNotApplicable={latencyNotApplicable}
          />
        );
      }
    },
    {
      id: 'ingested',
      field: 'ingested',
      headerName: '# Ingested',
      description: 'Count of Ingested Events',
      width: 80,
      justifyEnd: true,
      renderCell: params => {
        return params.row.data.isLoadOnly ? (
          <span>N/A</span>
        ) : (
          <RenderEventCell count={params.row.data.events.ingested} />
        );
      },
      canSort: true,
      sortKey: 'ingested'
    },

    {
      id: 'loaded',
      field: 'loaded',
      headerName: '# Loaded',
      description: 'Count of Loaded Events',
      width: !withPipelineDetails ? 90 : 80,
      justifyEnd: true,
      renderCell: params => <RenderEventCell count={params.row.data.events.loaded} />,
      canSort: true,
      sortKey: 'loaded'
    },
    {
      id: 'failed',
      field: 'failed',
      headerName: '# Failed',
      description: 'Count of Failed Events',
      width: !withPipelineDetails ? 90 : 70,
      justifyEnd: true,
      renderCell: params => <RenderEventCell count={params.row.data.events.failed} />,
      canSort: true,
      sortKey: 'failed'
    },
    {
      id: 'status',
      field: 'status',
      headerName: 'Status',
      width: 200,
      minWidth: 200,
      isFlexible: !withPipelineDetails,
      justifyEnd: true,
      withoutBorder: true,
      renderCell: params => <GenericStatusLabel status={params.row.data.status} />
    }
  ];
};
